import { useEffect, useLayoutEffect, useRef, useState } from "react"
import type { AnimationEvent, HTMLAttributes, ReactNode, TransitionEvent } from "react"

import { cn } from "@/lib/utils"

import { isMorphEasing, MORPH_EXIT_FALLBACK_MS, setMorphWidth } from "./voice-morph-utils"
import styles from "./voice-stage.module.css"

type VoiceMorphMode = "focus" | "roll"

type VoiceMorphProps = Omit<HTMLAttributes<HTMLSpanElement>, "children" | "id"> & {
  /** What the face says. A new id morphs; the same id only remeasures. */
  id: string
  /** `focus` sharpens out of a blur (600 ms); `roll` rolls the line (220 ms). */
  mode: VoiceMorphMode
  children: ReactNode
}

type MorphGhost = {
  /** Newest wins: a later change replaces the ghost instead of stacking one. */
  seq: number
  children: ReactNode
}

function isOwnAnimation(event: AnimationEvent<HTMLSpanElement>): boolean {
  // The spinner inside a face has its own animation; only the face's counts.
  return event.target === event.currentTarget
}

function releaseWillChange(event: AnimationEvent<HTMLSpanElement>): void {
  event.currentTarget.style.willChange = "auto"
}

/**
 * One face changing into the next: a clip box holding the current content and
 * at most one outgoing ghost, stacked on top of each other, with the box's
 * width eased from the one to the other.
 *
 * The box carries `className`; every other attribute — `data-*`, a test id —
 * lands on the current face, so a query always resolves to what is on screen
 * and never to the ghost, which is `aria-hidden` and carries nothing.
 */
export function VoiceMorph({ children, className, id, mode, ...rest }: VoiceMorphProps) {
  const boxRef = useRef<HTMLSpanElement>(null)
  const faceRef = useRef<HTMLSpanElement>(null)
  const shownRef = useRef<{ id: string; children: ReactNode } | null>(null)
  const seqRef = useRef(0)
  // Until when the box is easing to its last morph. Renders and resizes inside
  // that window must not remeasure: a remeasure takes its width at once.
  const easeUntilRef = useRef(0)
  const [ghost, setGhost] = useState<MorphGhost | null>(null)
  const [changed, setChanged] = useState(false)

  // Only a new id is a morph. The overlay re-renders every second for the clock
  // and hands over fresh children each time; measuring costs forced layouts, so
  // those renders are left alone and the resize observer owns every remeasure.
  useLayoutEffect(() => {
    const box = boxRef.current
    const face = faceRef.current
    if (!box || !face) return

    const shown = shownRef.current
    if (shown !== null && shown.id === id) return
    const morphing = shown !== null
    if (morphing) {
      seqRef.current += 1
      setGhost({ children: shown.children, seq: seqRef.current })
      setChanged(true)
      easeUntilRef.current = performance.now() + MORPH_EXIT_FALLBACK_MS
    }
    // The first paint takes its width without a tween.
    setMorphWidth(box, face, morphing)
  }, [id])

  // After the effect above has read the previous face: remember this one, as
  // it was last rendered, for the ghost the next morph leaves behind.
  useLayoutEffect(() => {
    shownRef.current = { children, id }
  })

  // A late font, a longer word in another locale, a smaller stage: the face or
  // the room it has changes on its own, and the box follows without animating.
  // Re-bound per id, because a morph replaces the element being watched.
  useEffect(() => {
    const box = boxRef.current
    const face = faceRef.current
    if (!box || !face || typeof ResizeObserver === "undefined") return

    // Observing reports once straight away, and the pill resizes all through
    // a morph: neither is a remeasure, and taking one would cut the ease short.
    const observer = new ResizeObserver(() => {
      if (performance.now() < easeUntilRef.current || isMorphEasing(box)) return
      setMorphWidth(box, face, false)
    })
    observer.observe(face)
    // The parent carries how much width is left for the face to wrap into.
    if (box.parentElement) observer.observe(box.parentElement)
    return () => observer.disconnect()
  }, [id])

  // No layer can get stuck: if `animationend` never arrives, sweep the ghost.
  useEffect(() => {
    if (!ghost) return
    const timer = window.setTimeout(() => setGhost(null), MORPH_EXIT_FALLBACK_MS)
    return () => window.clearTimeout(timer)
  }, [ghost])

  // The ease is over (or the fallback ran out with nothing to ease): open the
  // window again and take whatever remeasure it held back.
  const settle = () => {
    easeUntilRef.current = 0
    const box = boxRef.current
    const face = faceRef.current
    if (box && face) setMorphWidth(box, face, false)
  }

  useEffect(() => {
    if (!changed) return
    const timer = window.setTimeout(settle, MORPH_EXIT_FALLBACK_MS)
    return () => window.clearTimeout(timer)
  }, [changed, id])

  const handleBoxTransitionEnd = (event: TransitionEvent<HTMLSpanElement>) => {
    if (event.target === event.currentTarget && event.propertyName === "width") settle()
  }

  const handleFaceEnd = (event: AnimationEvent<HTMLSpanElement>) => {
    if (isOwnAnimation(event)) releaseWillChange(event)
  }

  const handleGhostEnd = (event: AnimationEvent<HTMLSpanElement>) => {
    if (!isOwnAnimation(event)) return
    releaseWillChange(event)
    setGhost(null)
  }

  return (
    <span
      ref={boxRef}
      data-mode={mode}
      className={cn(
        styles.morph,
        mode === "focus" ? styles.morphFocus : styles.morphRoll,
        className
      )}
      onTransitionEnd={handleBoxTransitionEnd}
    >
      <span
        key={id}
        ref={faceRef}
        className={styles.morphFace}
        data-enter={changed ? "" : undefined}
        onAnimationEnd={handleFaceEnd}
        {...rest}
      >
        {children}
      </span>
      {ghost && (
        <span
          key={`ghost-${ghost.seq}`}
          aria-hidden="true"
          className={styles.morphFace}
          data-exit=""
          onAnimationEnd={handleGhostEnd}
        >
          {ghost.children}
        </span>
      )}
    </span>
  )
}
